import type { PlannerStore, PushSubscriptionPayload } from './store/types';

export type PushRegistrationStatus = 'unsupported' | 'unconfigured' | 'blocked' | 'subscribed';

const DEVICE_ID_KEY = 'wingmate:device-id';

export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, created);
    return created;
  } catch {
    return 'device-unavailable';
  }
}

function urlBase64ToBuffer(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0))).buffer;
}

function serializeSubscription(subscription: PushSubscription): PushSubscriptionPayload {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint ?? subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
  };
}

export function pushNotificationsSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushNotificationsSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function enablePushNotifications(store: PlannerStore): Promise<PushRegistrationStatus> {
  if (!pushNotificationsSupported()) return 'unsupported';
  const config = await store.getPushConfig();
  if (!config.enabled || !config.publicKey) return 'unconfigured';

  const permission = Notification.permission === 'default'
    ? await Notification.requestPermission()
    : Notification.permission;
  if (permission !== 'granted') return 'blocked';

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToBuffer(config.publicKey),
  });

  await store.savePushSubscription({
    subscription: serializeSubscription(subscription),
    deviceId: getDeviceId(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    userAgent: navigator.userAgent,
  });
  return 'subscribed';
}

export async function disablePushNotifications(store: PlannerStore): Promise<void> {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) return;
  await store.removePushSubscription(subscription.endpoint);
  await subscription.unsubscribe();
}
